<template lang="pug">
  div(class="dynamic-autocomplete-wrapper")
    button(
      :class="activatorClasses"
      :disabled="isDisabled || value"
      class="dynamic-autocomplete__activator"
      @click.stop="onFieldClick"
    )
      template(v-if="!toolbarsMap[field.id]")
        span(
          class="text-12px dark dynamic-autocomplete__activator-text"
          :class="{ 'color-primary': value }"
          :style="selectTextStyles"
        ) {{ selectedLabel }}
        e-svg-icon(class="dynamic-autocomplete__icon") personalcard2
      div(
        v-else
        class="dynamic-autocomplete__body"
      )
        div(class="dynamic-autocomplete__head")
          div(class="dynamic-autocomplete__title") {{ $t('Verification data') }}
          div(class="dynamic-autocomplete__description") {{ $t('Select which info receiver will fill with Diia or BankId') }}
        div(
          v-for="option in verifiedOptions"
          :key="option.id"
          class="dynamic-autocomplete__options-item"
        )
          button(
            @click.stop="selectOption(option)"
            class="d-flex align-center w-100"
          )
            span(class="d-flex align-center")
              e-svg-icon(
                :name="selectedOption && option.value === selectedOption.value ? 'selected-radio-button' : 'circle-empty'"
                class="dynamic-autocomplete__options-item-icon"
              )
            span(class="dynamic-autocomplete__activator-text ml-2") {{ option.label }}
</template>

<script>
import { mapGetters } from 'vuex'
import responsive from '~/mixins/pages/responsive'
import contentDialog from '~/mixins/dialogs/contentDialog'

export default {
  name: 'TemplatesDynamicAutocomplete',
  mixins: [responsive, contentDialog],
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, null],
      default: null
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedOption: null
    }
  },
  computed: {
    ...mapGetters('templatefields', {
      toolbarsMap: 'toolbarsMap'
    }),
    verifiedOptions() {
      return [
        { value: 'fullName', label: 'ПІБ (прізвище, імʼя, по батькові)' },
        { value: 'identity', label: 'РНОКПП (ІПН)' }
      ]
    },
    selectedLabel() {
      return this.value || this.selectedOption?.label
    },
    activatorClasses() {
      return {
        filled: !!this.value,
        opened: this.toolbarsMap[this.field.id]
      }
    },
    selectTextStyles() {
      return { fontSize: this.xsDevice ? '6px' : `${this.fontSize}px` }
    }
  },
  mounted() {
    this.setAutocomplete()
  },
  methods: {
    setAutocomplete() {
      if (this.canEdit && !this.field.hint) {
        // creation of new autocomplete
        this.selectOption(this.verifiedOptions[0])
      } else if (this.field.documentFieldValue) {
        // autocomplete has saved value on document
        this.selectOption({
          label: this.field.documentFieldValue,
          value: 'APPLIED_DOCUMENT_VALUE'
        })
      } else {
        // setting selected autocomplete option
        const selectedOption = this._.find(this.field.options, opt => opt.selected)
        if (selectedOption) {
          this.selectOption(selectedOption)
        } else {
          const optionByLabel = this._.find(
            this.verifiedOptions,
            option => option.label === this.field.hint
          )
          this.selectOption(optionByLabel)
        }
      }
    },
    onFieldClick() {
      if (this.canEdit) {
        // todo: write method on top of store commit
        this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
          id: this.field.id,
          value: true
        })
      } else {
        this.openModal()
      }
    },
    selectOption(option) {
      this.selectedOption = option
      if (this.canEdit && option?.label !== this.field.hint) {
        this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
          id: this.field.id,
          value: false
        })
        this.$store.commit('templatefields/SET_LAST_ACTIVE_FIELD_ID', this.field.id)
        this.$store.commit('templatefields/UPDATE_FIELD', {
          id: this.field.id,
          updatedData: {
            hint: this.selectedOption.label,
            options: this._.map(this.verifiedOptions, opt =>
              opt.value === option.value ? { ...opt, selected: true } : opt
            ),
            changed: true
          }
        })
      }
    },
    openModal() {
      this.contentDialog.open({
        component: 'm-autofill-data',
        width: '724px',
        removePadding: true,
        componentProps: {
          onSuccess: this.passData,
          field: this.selectedOption?.value
        }
      })
    },
    passData(data) {
      this.$emit('update:value', data)
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-autocomplete {
  &-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }
  &__body {
    width: 100%;
    height: 100%;

    padding: 8px 0;

    background: $white;
    border-radius: 8px;
    text-align: left;
  }

  &__title {
    font-family: 'e-Ukraine', serif;
    font-weight: 400;
    font-size: 12px;
    color: $text-dark;
  }

  &__description {
    font-family: 'e-Ukraine', serif;
    font-weight: 300;
    font-size: 10px;
    color: #676767;
  }

  &__head {
    margin-bottom: 8px;
  }

  &__activator {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;

    overflow: auto;

    width: 100%;
    height: 100%;

    border: 1px solid $primary;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.7);

    @media (max-width: map-get($breakpoints, 'sm')) {
      padding: 0;
      border-radius: 4px;
      font-size: 7px;
      overflow: hidden;
    }

    &.filled {
      background:
        linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
        rgba(255, 255, 255, 0.4);
    }
    &.opened {
      height: auto;
      background: $white;
    }
  }

  &__activator-text {
    word-break: break-word;
    text-align: left;
    font-weight: 300;

    @media (max-width: map-get($breakpoints, 'sm')) {
      line-height: 1;
    }
  }

  &__icon {
    @media (max-width: map-get($breakpoints, 'sm')) {
      max-width: 12px;
    }
  }

  &__input-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
  }

  &__input {
    width: 100%;
    height: auto;

    padding: 8px;
    border-radius: 8px;

    outline: none;
    background: $background-secondary-alternative;

    @media (max-width: map-get($breakpoints, 'xs')) {
      font-size: 7px;
      padding: 4px;
      height: 16px;
    }
  }

  &__input-icon {
    width: 16px;
    height: 16px;

    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    z-index: map-get($zIndexes, 'base');
  }

  &__options-item {
    width: 100%;
    height: 32px;

    padding: 6px;

    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 10px;
    font-family: 'e-Ukraine', serif;
    font-weight: 300;

    &-icon {
      @media (max-width: map-get($breakpoints, 'xs')) {
        max-width: 12px;
        max-height: 12px;
      }
    }
  }

  &__options-input {
    width: 100%;
    height: 100%;

    margin-left: 8px;

    outline: none;

    @media (max-width: map-get($breakpoints, 'xs')) {
      font-size: 7px;
      margin-left: 4px;
    }
  }

  &__options-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__error {
    font-size: 12px;
    color: $danger;

    @media (max-width: map-get($breakpoints, 'xs')) {
      font-size: 7px;
    }
  }
}
</style>
